import React, { useState } from 'react';
import { Shield, CheckCircle, AlertTriangle, FileText, Calendar, Eye, Download, RefreshCw, Award } from 'lucide-react';

const ENRMCCompliance = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Mock eNRMC Compliance data
  const enrmcData = {
    compliance: {
      overallScore: 94.5,
      lastAudit: '2025-01-15',
      nextAudit: '2025-04-15',
      status: 'Compliant',
      issues: 2,
      criticalIssues: 0
    },
    standards: [
      {
        id: 'ENRMC001',
        name: 'Medication Charting Completeness',
        description: 'All medication administrations must be charted within 1 hour',
        compliance: 98.2,
        status: 'Compliant',
        lastChecked: '2025-01-20',
        requirements: [
          'Medication name and strength recorded',
          'Dose administered documented',
          'Time of administration noted',
          'Administering staff identified',
          'Resident response documented'
        ]
      },
      {
        id: 'ENRMC002',
        name: 'Controlled Drug Register (S8)',
        description: 'S8 medications must be recorded in controlled drug register',
        compliance: 100.0,
        status: 'Compliant',
        lastChecked: '2025-01-22',
        requirements: [
          'All S8 medications recorded',
          'Witness signatures obtained',
          'Balance reconciliation completed',
          'Variance reporting documented',
          'Destruction records maintained'
        ]
      },
      {
        id: 'ENRMC003',
        name: 'Medication Error Reporting',
        description: 'All medication errors must be reported within 24 hours',
        compliance: 89.5,
        status: 'Needs Improvement',
        lastChecked: '2025-01-18',
        requirements: [
          'Error details documented',
          'Root cause analysis completed',
          'Corrective actions implemented',
          'Follow-up monitoring scheduled',
          'Staff education provided'
        ]
      },
      {
        id: 'ENRMC004',
        name: 'Medication Review Process',
        description: 'Regular medication reviews must be conducted',
        compliance: 92.8,
        status: 'Compliant',
        lastChecked: '2025-01-19',
        requirements: [
          'Monthly medication reviews scheduled',
          'Pharmacist involvement documented',
          'Resident/family consultation recorded',
          'Changes implemented and documented',
          'Outcomes monitored and evaluated'
        ]
      },
      {
        id: 'ENRMC005',
        name: 'Medication Storage and Security',
        description: 'Medications must be stored securely and appropriately',
        compliance: 96.1,
        status: 'Compliant',
        lastChecked: '2025-01-21',
        requirements: [
          'Temperature-controlled storage maintained',
          'Access restricted to authorized staff',
          'Expiry dates monitored and managed',
          'Controlled drugs in locked cabinet',
          'Regular security audits conducted'
        ]
      },
      {
        id: 'ADHA-CP001',
        name: 'ADHA CPv3.0 — Order Identification',
        description: 'Each eNRMC order has a unique UUID compliant with ADHA Conformance Profile v3.0',
        compliance: 100.0,
        status: 'Compliant',
        lastChecked: new Date().toISOString().split('T')[0],
        requirements: [
          'Unique order ID (UUID) assigned on creation',
          'Order ID immutable after creation',
          'Order ID referenced in all administration records',
          'Order ID included in audit trail'
        ]
      },
      {
        id: 'ADHA-CP002',
        name: 'ADHA CPv3.0 — Prescriber HPI-I',
        description: 'Prescriber Healthcare Provider Identifier (Individual) validated as 16-digit format',
        compliance: 100.0,
        status: 'Compliant',
        lastChecked: new Date().toISOString().split('T')[0],
        requirements: [
          'HPI-I mandatory for all medication orders',
          '16-digit format validation enforced',
          'HPI-I displayed in masked format (privacy)',
          'HPI-I linked to prescriber registration'
        ]
      },
      {
        id: 'ADHA-CP003',
        name: 'ADHA CPv3.0 — Facility & Pharmacy HPI-O',
        description: 'Facility and Pharmacy Healthcare Provider Identifier (Organisation) validated',
        compliance: 100.0,
        status: 'Compliant',
        lastChecked: new Date().toISOString().split('T')[0],
        requirements: [
          'Facility HPI-O mandatory on all orders',
          'Pharmacy HPI-O recorded when applicable',
          '16-digit format validation on both',
          'HPI-O linked to registered organisations'
        ]
      },
      {
        id: 'ADHA-CP004',
        name: 'ADHA CPv3.0 — SNOMED CT-AU Routes',
        description: 'Route of administration uses SNOMED CT-AU coded values',
        compliance: 100.0,
        status: 'Compliant',
        lastChecked: new Date().toISOString().split('T')[0],
        requirements: [
          'Route coded with SNOMED CT-AU concepts',
          'Route description auto-populated from code',
          'Coded routes cover all common administration routes',
          'Custom route text available for edge cases'
        ]
      },
      {
        id: 'ADHA-CP011',
        name: 'ADHA CPv3.0 — Allergy Safety Checking',
        description: 'Real-time allergy checks at prescribe-time with override audit trail',
        compliance: 100.0,
        status: 'Compliant',
        lastChecked: new Date().toISOString().split('T')[0],
        requirements: [
          'Auto-fire allergy check on new medication order',
          'Block if known allergy match detected',
          'Override requires reason + senior clinician acknowledgement',
          'Cross-reactivity warnings (e.g. penicillin → cephalosporins)',
          'Override fully audit-logged with timestamp'
        ]
      },
      {
        id: 'ADHA-CP012',
        name: 'ADHA CPv3.0 — Drug Interaction Checking',
        description: 'Auto-check against current medication list using drug interaction database',
        compliance: 100.0,
        status: 'Compliant',
        lastChecked: new Date().toISOString().split('T')[0],
        requirements: [
          'Interaction check runs on every new/updated order',
          'ATC classification database for drug class mapping',
          'Critical interactions block order (require override)',
          'Major/moderate interactions displayed as warnings',
          'Duplicate therapy detection (same drug class already prescribed)'
        ]
      },
      {
        id: 'ADHA-CP013',
        name: 'ADHA CPv3.0 — Psychotropic & Polypharmacy',
        description: 'Psychotropic auto-detection via ATC codes; polypharmacy alerting at ≥9 medications',
        compliance: 100.0,
        status: 'Compliant',
        lastChecked: new Date().toISOString().split('T')[0],
        requirements: [
          'Psychotropic auto-detect from ATC N05/N06 codes',
          'NQIP Indicator 5 auto-flag on psychotropic prescribing',
          '3-monthly review schedule auto-created for psychotropics',
          'Polypharmacy alert at ≥9 active medications',
          'Pharmacist review task auto-created for polypharmacy'
        ]
      }
    ],
    residents: [
      {
        id: 'RES001',
        name: 'Margaret Johnson',
        room: 'A101',
        complianceScore: 96.8,
        lastReview: '2025-01-20',
        medications: 8,
        issues: 0,
        status: 'Compliant'
      },
      {
        id: 'RES002',
        name: 'Robert Smith',
        room: 'A102',
        complianceScore: 89.2,
        lastReview: '2025-01-18',
        medications: 5,
        issues: 1,
        status: 'Needs Attention'
      },
      {
        id: 'RES003',
        name: 'Mary Williams',
        room: 'B201',
        complianceScore: 94.5,
        lastReview: '2025-01-22',
        medications: 6,
        issues: 0,
        status: 'Compliant'
      },
      {
        id: 'RES004',
        name: 'John Brown',
        room: 'B202',
        complianceScore: 87.3,
        lastReview: '2025-01-15',
        medications: 4,
        issues: 2,
        status: 'Needs Attention'
      }
    ],
    audits: [
      {
        id: 'AUDIT001',
        date: '2025-01-15',
        type: 'Internal',
        auditor: 'Dr. Sarah Mitchell',
        score: 94.5,
        status: 'Completed',
        findings: 3,
        criticalFindings: 0,
        recommendations: 5
      },
      {
        id: 'AUDIT002',
        date: '2024-10-20',
        type: 'External',
        auditor: 'ACQSC Inspector',
        score: 91.2,
        status: 'Completed',
        findings: 5,
        criticalFindings: 1,
        recommendations: 8
      },
      {
        id: 'AUDIT003',
        date: '2024-07-15',
        type: 'Internal',
        auditor: 'Quality Manager',
        score: 88.7,
        status: 'Completed',
        findings: 7,
        criticalFindings: 0,
        recommendations: 6
      }
    ]
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Compliant': return 'bg-green-100 text-green-800';
      case 'Needs Improvement': return 'bg-yellow-100 text-yellow-800';
      case 'Needs Attention': return 'bg-orange-100 text-orange-800';
      case 'Non-Compliant': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 90) return 'text-blue-600';
    if (score >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const filteredResidents = enrmcData.residents.filter(resident =>
    resident.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resident.room.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSync = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              eNRMC Compliance
              <Award className="w-8 h-8 text-purple-200" />
            </h1>
            <p className="mt-2 opacity-90">
              Electronic National Residential Medication Chart compliance monitoring and reporting
            </p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={handleSync}
              disabled={loading}
              className="bg-white text-purple-600 px-4 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Syncing...' : 'Sync Data'}
            </button>
            <button 
              onClick={onClose}
              className="text-white hover:text-purple-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>
      </div>

      {/* Compliance Overview */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-600" />
          Overall Compliance Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Award className="w-6 h-6 text-purple-600" />
              <div>
                <h4 className="font-semibold text-purple-800">Overall Score</h4>
                <p className="text-2xl font-bold text-purple-600">{enrmcData.compliance.overallScore}%</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <h4 className="font-semibold text-green-800">Status</h4>
                <p className="text-lg font-bold text-green-600">{enrmcData.compliance.status}</p>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <div>
                <h4 className="font-semibold text-yellow-800">Issues</h4>
                <p className="text-2xl font-bold text-yellow-600">{enrmcData.compliance.issues}</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-blue-600" />
              <div>
                <h4 className="font-semibold text-blue-800">Next Audit</h4>
                <p className="text-sm font-bold text-blue-600">{enrmcData.compliance.nextAudit}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 py-2">
            {[
              { id: 'overview', label: 'Overview', icon: Shield },
              { id: 'standards', label: 'Standards', icon: FileText },
              { id: 'residents', label: 'Residents', icon: CheckCircle },
              { id: 'audits', label: 'Audits', icon: Calendar }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600 bg-purple-50 rounded-t-lg'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 rounded-t-lg'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Compliance Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Last audit date:</span>
                      <span className="font-medium">{enrmcData.compliance.lastAudit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Next audit date:</span>
                      <span className="font-medium">{enrmcData.compliance.nextAudit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Critical issues:</span>
                      <span className="font-medium text-red-600">{enrmcData.compliance.criticalIssues}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total issues:</span>
                      <span className="font-medium text-yellow-600">{enrmcData.compliance.issues}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Recent Activity</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Residents reviewed:</span>
                      <span className="font-medium">{enrmcData.residents.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Standards monitored:</span>
                      <span className="font-medium">{enrmcData.standards.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Audits completed:</span>
                      <span className="font-medium">{enrmcData.audits.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Compliance rate:</span>
                      <span className="font-medium text-green-600">{enrmcData.compliance.overallScore}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Standards Tab */}
          {activeTab === 'standards' && (
            <div className="space-y-4">
              {enrmcData.standards.map((standard) => (
                <div key={standard.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg text-gray-800">{standard.name}</h4>
                      <p className="text-sm text-gray-600 mb-3">{standard.description}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Compliance</p>
                          <p className={`text-lg font-bold ${getScoreColor(standard.compliance)}`}>
                            {standard.compliance}%
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Status</p>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(standard.status)}`}>
                            {standard.status}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Last Checked</p>
                          <p className="text-sm text-gray-600">{standard.lastChecked}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Requirements</p>
                          <p className="text-sm text-gray-600">{standard.requirements.length} items</p>
                        </div>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${standard.compliance}%` }}
                        ></div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3">
                        <h5 className="font-medium text-gray-800 mb-2">Requirements:</h5>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {standard.requirements.map((req, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Residents Tab */}
          {activeTab === 'residents' && (
            <div className="space-y-4">
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search residents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-3">
                {filteredResidents.map((resident) => (
                  <div key={resident.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <div>
                            <h4 className="font-semibold text-lg text-gray-800">{resident.name}</h4>
                            <p className="text-sm text-gray-600">Room: {resident.room}</p>
                          </div>
                          <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(resident.status)}`}>
                            {resident.status}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                          <div>
                            <p className="text-sm font-medium text-gray-700">Compliance Score</p>
                            <p className={`text-lg font-bold ${getScoreColor(resident.complianceScore)}`}>
                              {resident.complianceScore}%
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">Medications</p>
                            <p className="text-lg font-bold text-blue-600">{resident.medications}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">Issues</p>
                            <p className="text-lg font-bold text-yellow-600">{resident.issues}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">Last Review</p>
                            <p className="text-sm text-gray-600">{resident.lastReview}</p>
                          </div>
                        </div>

                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${resident.complianceScore}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <button 
                          onClick={() => console.log('View resident details')}
                          className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => console.log('Download report')}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audits Tab */}
          {activeTab === 'audits' && (
            <div className="space-y-4">
              {enrmcData.audits.map((audit) => (
                <div key={audit.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <div>
                          <h4 className="font-semibold text-lg text-gray-800">
                            {audit.type} Audit - {audit.date}
                          </h4>
                          <p className="text-sm text-gray-600">Auditor: {audit.auditor}</p>
                        </div>
                        <span className={`px-3 py-1 text-sm rounded-full ${
                          audit.status === 'Completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {audit.status}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Score</p>
                          <p className={`text-lg font-bold ${getScoreColor(audit.score)}`}>
                            {audit.score}%
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Findings</p>
                          <p className="text-lg font-bold text-yellow-600">{audit.findings}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Critical</p>
                          <p className="text-lg font-bold text-red-600">{audit.criticalFindings}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Recommendations</p>
                          <p className="text-lg font-bold text-blue-600">{audit.recommendations}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button 
                        onClick={() => console.log('View audit details')}
                        className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => console.log('Download audit report')}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ENRMCCompliance;
